import React from 'react';
import Icon from '@/components/ui/AppIcon';
import { toast } from 'sonner';
import type { LegalResource } from './ResourceContent';

type Props = { resource: LegalResource };

const typeIcons: Record<string, string> = {
  'PDF Guide': 'DocumentTextIcon',
  'Know Your Rights': 'ShieldCheckIcon',
  FAQ: 'QuestionMarkCircleIcon',
  Template: 'ClipboardDocumentIcon',
};

const typeColors: Record<string, string> = {
  'PDF Guide': 'bg-blue-50 text-blue-700',
  'Know Your Rights': 'bg-emerald-50 text-emerald-700',
  FAQ: 'bg-purple-50 text-purple-700',
  Template: 'bg-amber-50 text-amber-700',
};

const categoryColors: Record<string, string> = {
  'Immigration Law': 'text-rose-600',
  'Civic Rights': 'text-blue-600',
  'Advocacy Guides': 'text-amber-600',
  'Legal Aid': 'text-emerald-600',
};

export default function LegalResourceCard({ resource }: Props) {
  const handleDownload = () => {
    // Backend integration point: GET /api/resources/:id/download
    toast.success(`Downloading: ${resource.title}`, {
      description: 'Your resource is being prepared.',
    });
  };

  return (
    <div className="card-base card-hover flex flex-col p-5">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Icon
            name={typeIcons[resource.type] as any}
            size={22}
            variant="outline"
            className="text-primary"
          />
        </div>
        <span className={`badge-skill ${typeColors[resource.type]}`}>{resource.type}</span>
      </div>

      <p className={`text-xs font-semibold uppercase tracking-wider mb-1 ${categoryColors[resource.category]}`}>
        {resource.category}
      </p>
      <h3 className="text-base font-bold text-foreground mb-2 leading-snug line-clamp-2">
        {resource.title}
      </h3>
      <p className="text-sm text-zinc-500 leading-relaxed mb-4 flex-1 line-clamp-3">
        {resource.description}
      </p>

      {/* Languages */}
      <div className="flex flex-wrap gap-1.5 mb-4">
        {resource.language.map((lang) => (
          <span
            key={`lang-${resource.id}-${lang}`}
            className="px-2 py-0.5 rounded-md bg-zinc-100 text-zinc-600 text-xs font-medium"
          >
            {lang}
          </span>
        ))}
      </div>

      {/* Meta */}
      <div className="flex items-center justify-between text-xs text-muted-foreground mb-4 pt-3 border-t border-border">
        <span className="flex items-center gap-1">
          <Icon name="ArrowDownTrayIcon" size={12} variant="outline" />
          {resource.downloads.toLocaleString()} downloads
        </span>
        <span className="flex items-center gap-1">
          <Icon name="CalendarIcon" size={12} variant="outline" />
          Updated {resource.lastUpdated}
        </span>
      </div>

      <button onClick={handleDownload} className="btn-primary w-full text-xs py-2.5 justify-center" suppressHydrationWarning>
        <Icon name="ArrowDownTrayIcon" size={14} variant="outline" />
        Download Free
      </button>
    </div>
  );
}